'use client'

import type { FormEvent } from 'react'
import { useState } from 'react'
import type { InvestableAssetsRange } from '@nwwp/database'
import { Checkbox, Input } from '@nwwp/ui-shadcn'
import { createCareerBreakLead } from '~/actions/create-career-break-lead'
import { PhoneDisclosure } from '~/components/LeadMagnet/PhoneDisclosure'
import { NumberInput } from '~/components/NumberInput'
import { RangeSelectInput } from '~/components/RangeSelectInput'
import { getTrackingParams } from '~/utils/tracking'
import type { CareerBreakState } from '../types'

const FIELD_LABEL = 'text-subtitle font-bold text-neutral-darker uppercase'

/**
 * The results gate: the visitor sets their inputs freely, then unlocks the results.
 *
 * Submitting creates the Lead, which is what sends the results email downstream, so this
 * is the page's only capture point — a second form after the results would create a
 * duplicate Lead (`POST /api/leads` does not dedupe on email).
 *
 * Phone is required because the consent block is express written TCPA consent, and
 * `nwwp_career-break` is stamped CONFIRMED at Lead create (see
 * packages/salesforce/src/mappers/tcpa-source-map.ts).
 */

/** Canonical qualification buckets. The prototype's four collapse $100K–$500K, which has no single stored value. */
const AUM_OPTIONS: { value: InvestableAssetsRange; label: string }[] = [
  { value: 'UNDER_100K', label: 'Less than $100K' },
  { value: 'FROM_100_TO_250K', label: '$100K-$249K' },
  { value: 'FROM_250_TO_500K', label: '$250K-$499K' },
  { value: 'FROM_500K_TO_1M', label: '$500K-$999K' },
  { value: 'OVER_1M', label: '$1MM+' },
]

export function UnlockGate({ state, onUnlock }: { state: CareerBreakState; onUnlock: () => void }) {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [income, setIncome] = useState('')
  const [assets, setAssets] = useState<InvestableAssetsRange | ''>('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [wantsCall, setWantsCall] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [showFieldErrors, setShowFieldErrors] = useState(false)

  const incomeValue = Number(income)
  const incomeValid = income.length > 0 && Number.isFinite(incomeValue) && incomeValue > 0
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  const phoneDigits = phone.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '')
  const phoneValid = /^[2-9]\d{9}$/.test(phoneDigits)
  const valid =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    incomeValid &&
    assets !== '' &&
    emailValid &&
    phoneValid

  const firstNameError = showFieldErrors && firstName.trim().length === 0 ? 'Please enter your first name.' : undefined
  const lastNameError = showFieldErrors && lastName.trim().length === 0 ? 'Please enter your last name.' : undefined
  const incomeError = showFieldErrors && !incomeValid ? 'Please enter your household income.' : undefined
  const assetsError = showFieldErrors && assets === '' ? 'Please select your estimated investable assets.' : undefined
  const emailError = showFieldErrors
    ? email.length === 0
      ? 'Please enter your email address.'
      : emailValid
        ? undefined
        : 'Please enter a valid email address.'
    : undefined
  const phoneError = showFieldErrors
    ? phone.length === 0
      ? 'Please enter your phone number.'
      : phoneValid
        ? undefined
        : 'Please enter a valid phone number.'
    : undefined

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (!valid || submitting) {
      if (!valid) setShowFieldErrors(true)
      return
    }
    setSubmitting(true)
    setSubmitError(null)
    try {
      const created = await createCareerBreakLead(
        {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim(),
          phone: `+1${phoneDigits}`,
          // The gate asks for the household total; the panel asks each earner separately.
          // The Lead takes this figure as-is and the model keeps the panel's inputs, so a
          // couple's partner income is not counted twice.
          annual_income: incomeValue,
          investable_assets_range: assets,
          wants_call: wantsCall,
        },
        state,
        getTrackingParams()
      )
      if (!created) throw new Error('Lead creation failed')
      onUnlock()
    } catch (error) {
      console.error('Career Break lead creation failed', error)
      setSubmitError('Something went wrong. Please try again.')
      setSubmitting(false)
    }
  }

  return (
    <section className="w-full rounded-xl border border-border bg-card p-6 shadow-lg tablet:p-8">
      <h2 className="text-title-sm text-gray-darker">Unlock the calculator</h2>

      <form noValidate onSubmit={handleSubmit} className="mt-5 flex flex-col gap-4">
        <div className="flex flex-col gap-3 tablet:flex-row">
          <div className="flex flex-1 flex-col gap-2">
            <label htmlFor="cb-first-name" className={FIELD_LABEL}>
              First Name
            </label>
            <Input
              id="cb-first-name"
              name="first_name"
              type="text"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              error={firstNameError}
            />
          </div>
          <div className="flex flex-1 flex-col gap-2">
            <label htmlFor="cb-last-name" className={FIELD_LABEL}>
              Last Name
            </label>
            <Input
              id="cb-last-name"
              name="last_name"
              type="text"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              error={lastNameError}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="cb-income" className={FIELD_LABEL}>
            Household Income
          </label>
          <NumberInput
            id="cb-income"
            name="annual_income"
            mask="numeric"
            prefix="$"
            placeholder="120,000"
            value={income}
            onChange={(e) => setIncome(e.target.value)}
            error={incomeError}
          />
        </div>

        <RangeSelectInput
          id="cb-investable-assets"
          name="investable_assets_range"
          label="Investable Assets"
          labelClassName={FIELD_LABEL}
          value={assets}
          onChange={(value) => setAssets(value as InvestableAssetsRange)}
          options={AUM_OPTIONS}
          error={assetsError}
        />

        <div className="flex flex-col gap-2">
          <label htmlFor="cb-email" className={FIELD_LABEL}>
            Email
          </label>
          <Input
            id="cb-email"
            name="email"
            type="email"
            autoComplete="email"
            placeholder="you@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={emailError}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="cb-phone" className={FIELD_LABEL}>
            Phone Number
          </label>
          <NumberInput
            id="cb-phone"
            name="phone"
            mask="phone"
            placeholder="(555) 555-5555"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            error={phoneError}
          />
        </div>

        <label className="flex items-start gap-2.5 text-body-sm text-neutral-dark">
          <Checkbox
            checked={wantsCall}
            onCheckedChange={(checked) => setWantsCall(checked === true)}
            aria-label="I'd also like a free 20-minute call with an advisor about this."
            className="mt-0.5"
          />
          I&apos;d also like a free 20-minute call with an advisor about this.
        </label>

        {/* The approved consent block, shared with /schedule_form and the lead magnets. */}
        <PhoneDisclosure ctaText="See my results" className="text-body-xs text-neutral-dark" />

        {submitError ? (
          <p role="alert" className="text-body-sm text-red">
            {submitError}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={submitting}
          className="self-start rounded-full bg-green-lighter-2 px-7 py-3.5 text-body-sm-bold text-black uppercase disabled:opacity-40"
        >
          {submitting ? 'Unlocking…' : 'See my results →'}
        </button>
      </form>
    </section>
  )
}

/** Shown after a successful gate submit. Delivery depends on Customer.io message IDs being set. */
export function ResultsEmailedNote() {
  return (
    <section className="rounded-xl border border-border bg-card p-6 tablet:p-8">
      <h2 className="text-title-sm text-gray-darker">Thanks — your results are unlocked.</h2>
      <p className="mt-2 text-body-sm text-neutral-dark">
        A copy may also arrive by email so you can come back to them as you decide.
      </p>
    </section>
  )
}
